import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { Card } from "@mui/material";
import { Box } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { useDataProvider } from "react-admin";

/**
 * VersionChart renders the main ECharts bar chart for tracked CVEs by version.
 * It fetches data from the API and initializes the chart inside a Card.
 */
interface VersionChartProps {
  releaseName: string;
}

const VersionChart: React.FC<VersionChartProps> = ({ releaseName }) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const dataProvider = useDataProvider();
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    let isMounted = true;
    let chartData: any = null;

    const fetchData = async () => {
      try {
        const response = await dataProvider.getChart(
          "cve/analytics/version-chart",
          {
            releaseName: releaseName,
          }
        );
        if (!isMounted) return;
        chartData = response;
        renderChart();
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching dashboard data:", error);
        }
      }
    };

    const renderChart = () => {
      if (!chartRef.current || !chartData) return;
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }

      // Define severity colors to match trackedCVE.tsx
      const severityColors = {
        CRITICAL: "#D32F2F",
        HIGH: "#F57C00",
        MEDIUM: "#FBC02D",
        LOW: "#FFF176",
        NONE: "#BDBDBD",
      };

      const option: echarts.EChartsOption = {
        grid: {
          top: "100em",
          bottom: "40em",
        },
        title: {
          text: "Tracked CVEs by Version",
          left: "center",
          top: "15em",
          textStyle: {
            color: theme.palette.mode === "dark" ? "#fff" : "#222",
            fontWeight: "bold",
            fontSize: "1.5em",
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        legend: {
          data: chartData.severities,
          top: "60em",
          textStyle: {
            color: theme.palette.mode === "dark" ? "#fff" : "#222",
            fontWeight: "bold",
            fontSize: 12,
          },
        },
        toolbox: {
          show: true,
          orient: "vertical",
          left: "right",
          top: "bottom",
          feature: {
            mark: { show: true },
            dataView: { show: true, readOnly: false },
            magicType: { show: true, type: ["line", "bar", "stack"] },
            restore: { show: true },
            saveAsImage: { show: true },
          },
        },
        xAxis: [
          {
            type: "category",
            axisTick: { show: false },
            data: chartData.versions,
            axisLabel: {
              color: theme.palette.mode === "dark" ? "#fff" : "#222",
              fontWeight: "bold",
              fontSize: 14,
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            axisLabel: {
              color: theme.palette.mode === "dark" ? "#fff" : "#222",
              fontWeight: "bold",
              fontSize: 14,
            },
          },
        ],
        series: chartData.series.map((series: any) => ({
          ...series,
          type: "bar",
          barGap: 0,
          label: { show: false },
          emphasis: { focus: "series" },
          itemStyle: {
            color: severityColors[series.name as keyof typeof severityColors],
          },
        })),
      };

      chartInstance.current.setOption(option);
    };

    fetchData();

    return () => {
      isMounted = false;
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [theme.palette.mode, dataProvider, releaseName]);

  return (
    <Card sx={{ height: 400, width: "100%" }}>
      <Box ref={chartRef} sx={{ height: "100%", width: "100%" }} />
    </Card>
  );
};

export default VersionChart;
